// ---------------------------------------------------------------------------
// Smart-guide snapping for the label canvas.
//
// Pure geometry: given where the user has dragged an element, return the position
// it should settle at plus the guide lines to draw. Kept out of the component so
// the alignment rules stay testable and the canvas stays about rendering.
// ---------------------------------------------------------------------------

import { elementSizeMm, contentBox } from "./labelGeometry";

export const DEFAULT_TOLERANCE_MM = 0.6;

/**
 * Lines a dragged element can snap to, as { at, kind } in mm.
 * `kind` only affects how the guide is drawn.
 */
export function snapTargets(label, others, dpmm) {
  const b = contentBox(label);
  const cx = label.widthMm / 2;
  const cy = label.heightMm / 2;

  const x = [
    { at: 0, kind: "edge" },
    { at: label.widthMm, kind: "edge" },
    { at: cx, kind: "center" },
  ];
  const y = [
    { at: 0, kind: "edge" },
    { at: label.heightMm, kind: "edge" },
    { at: cy, kind: "center" },
  ];

  if (label.paddingMm > 0) {
    x.push({ at: b.left, kind: "padding" }, { at: b.right, kind: "padding" });
    y.push({ at: b.top, kind: "padding" }, { at: b.bottom, kind: "padding" });
  }

  others.forEach((o) => {
    const s = elementSizeMm(o, dpmm);
    x.push(
      { at: o.xMm, kind: "element" },
      { at: o.xMm + s.wMm / 2, kind: "element" },
      { at: o.xMm + s.wMm, kind: "element" }
    );
    y.push(
      { at: o.yMm, kind: "element" },
      { at: o.yMm + s.hMm / 2, kind: "element" },
      { at: o.yMm + s.hMm, kind: "element" }
    );
  });

  return { x, y };
}

/** Best snap for one axis, comparing the element's near/centre/far edge to every target. */
function snapAxis(pos, extent, targets, tolerance) {
  let best = null;
  targets.forEach((t) => {
    [
      ["near", 0],
      ["center", extent / 2],
      ["far", extent],
    ].forEach((pair) => {
      const candidate = t.at - pair[1];
      const delta = Math.abs(candidate - pos);
      if (delta <= tolerance && (!best || delta < best.delta)) {
        best = { pos: candidate, delta, guide: { at: t.at, kind: t.kind } };
      }
    });
  });
  return best;
}

/**
 * @returns {{xMm:number, yMm:number, guides:Array<{axis:'x'|'y',at:number,kind:string}>}}
 */
export function snapPosition(opts) {
  const { el, xMm, yMm, label, others } = opts;
  const tolerance = opts.tolerance == null ? DEFAULT_TOLERANCE_MM : opts.tolerance;

  const size = elementSizeMm(el, label.dpmm);
  const targets = snapTargets(label, others, label.dpmm);

  const sx = snapAxis(xMm, size.wMm, targets.x, tolerance);
  const sy = snapAxis(yMm, size.hMm, targets.y, tolerance);

  const guides = [];
  if (sx) guides.push({ axis: "x", at: sx.guide.at, kind: sx.guide.kind });
  if (sy) guides.push({ axis: "y", at: sy.guide.at, kind: sy.guide.kind });

  return {
    xMm: Number((sx ? sx.pos : xMm).toFixed(2)),
    yMm: Number((sy ? sy.pos : yMm).toFixed(2)),
    guides,
  };
}
